import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Icons, Spinner } from '../../components/ui.tsx';

/**
 * A generated PDF, shown without leaving the app.
 *
 * Opening a blob URL in a new tab is simpler, but the tab is opened after an
 * await — by which time the browser no longer treats it as a response to the
 * click, and blocks it. An overlay always works, and keeps the sheet
 * underneath rather than scattering tabs.
 *
 * The same frame does the printing, so "view it" and "print it" are the same
 * document rather than two conversions that might differ.
 */
export function PdfPreview({
  title,
  url,
  onClose,
  onDownload,
}: {
  title: string;
  /** Null while the PDF is still being produced. */
  url: string | null;
  onClose: () => void;
  onDownload: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const print = () => {
    const frame = document.querySelector<HTMLIFrameElement>('[data-pdf-frame]');
    frame?.contentWindow?.focus();
    frame?.contentWindow?.print();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex flex-col bg-[rgba(9,17,28,0.82)]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="flex shrink-0 items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm font-medium text-white">{title}</p>
          <p className="text-micro text-[var(--blue-200)]">PDF preview</p>
        </div>

        <Button
          onClick={print}
          disabled={!url}
          className="border-white/25 bg-white/10 text-white hover:bg-white/20"
        >
          Print
        </Button>
        <Button
          icon={<Icons.Download />}
          onClick={onDownload}
          disabled={!url}
          className="border-white/25 bg-white/10 text-white hover:bg-white/20"
        >
          Download
        </Button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the preview"
          className="rounded-md p-2 text-white/80 transition-fast hover:bg-white/15 hover:text-white"
        >
          <Icons.Close size={20} />
        </button>
      </header>

      <div className="min-h-0 flex-1 px-4 pb-4">
        <div className="flex h-full items-center justify-center overflow-hidden rounded-xl bg-surface">
          {url ? (
            <iframe data-pdf-frame src={url} title={title} className="h-full w-full border-0" />
          ) : (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Spinner />
              <p className="text-body-sm text-muted">Producing the PDF…</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
